/**
 * useOddsComparison Hook
 * Shared between web and mobile for fetching multi-book odds comparison data.
 * Called lazily after picks are generated -- not on mount.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import type { HttpClient } from '../api/httpClient';
import type { BestOddsResult } from '../oddsUtils';

// Re-export for convenience
export { buildComparisonKey } from '../oddsUtils';

// =============================================================================
// Types
// =============================================================================

interface GameComparison {
  gameId: string;
  props: BestOddsResult[];
}

export interface UseOddsComparisonOptions {
  httpClient: HttpClient;
}

export interface UseOddsComparisonResult {
  /** Map of lookup key -> comparison data. Key: "player|market|line|direction" */
  comparisons: Map<string, BestOddsResult>;
  loading: boolean;
  error: string | null;
  /** Fetch comparison data for the given games. Call after picks are generated. */
  fetchComparison: (gameIds: string[], sportsbook: string) => Promise<void>;
}

// =============================================================================
// Hook
// =============================================================================

export function useOddsComparison(options: UseOddsComparisonOptions): UseOddsComparisonResult {
  const { httpClient } = options;

  const [comparisons, setComparisons] = useState<Map<string, BestOddsResult>>(new Map());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);

  useEffect(() => {
    // Re-arm on every mount: StrictMode's dev unmount/remount would otherwise leave it false.
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const fetchComparison = useCallback(
    async (gameIds: string[], sportsbook: string) => {
      if (gameIds.length === 0) return;

      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          gameIds: gameIds.join(','),
          sportsbook,
        });

        const res = await httpClient.rawFetch(`/api/odds/compare?${params}`);

        if (!res.ok) {
          throw new Error(`Failed to fetch odds comparison: ${res.status}`);
        }

        const data = (await res.json()) as { comparisons?: GameComparison[] };
        const gameComparisons: GameComparison[] = data.comparisons || [];

        // Build lookup map: "player|market|line|direction" -> BestOddsResult
        const map = new Map<string, BestOddsResult>();
        for (const game of gameComparisons) {
          for (const prop of game.props) {
            const key = `${prop.player}|${prop.market}|${prop.line ?? 'null'}|${prop.direction}`;
            map.set(key, prop);
          }
        }

        if (isMountedRef.current) {
          setComparisons(map);
        }
      } catch (err) {
        if (isMountedRef.current) {
          setError((err as Error).message);
        }
      } finally {
        if (isMountedRef.current) {
          setLoading(false);
        }
      }
    },
    [httpClient],
  );

  return { comparisons, loading, error, fetchComparison };
}
